import React, { useState, useRef, useEffect } from 'react';
import StudioShell from '@/components/studio/StudioShell';
import { Send, Bot, User, Loader2, Sparkles, ChevronRight } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { API_BASE as API } from '@/utils/api';

const SUGGESTED_QUESTIONS = [
  'Jak stworzyć profil hodowli?',
  'Jak działa Booster?',
  'Jak połączyć Facebooka?',
  'Jak dodać miot?',
  'Jak odpowiedzieć na zapytanie klienta?',
  'Co daje moduł Statystyki?',
  'Jak opublikować post na FB?',
  'Jak edytować ogłoszenie?',
];

export default function PomocPage() {
  const { token } = useAuth();
  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      content: 'Witaj! Jestem asystentem ApisAI. Pomogę Ci w korzystaniu z platformy. Zapytaj mnie o cokolwiek - jak dodać miot, jak działa Booster, gdzie znaleźć statystyki, jak połączyć Facebooka i wiele więcej!',
    },
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const sendMessage = async (text) => {
    const msg = text || input.trim();
    if (!msg || loading) return;

    setMessages((prev) => [...prev, { role: 'user', content: msg }]);
    setInput('');
    setLoading(true);

    try {
      const res = await fetch(`${API}/api/assistant/ask`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ message: msg, session_id: sessionId, history: messages.slice(-10) }),
      });
      if (res.ok) {
        const data = await res.json();
        setMessages((prev) => [...prev, { role: 'assistant', content: data.reply }]);
        if (data.session_id) setSessionId(data.session_id);
      } else {
        setMessages((prev) => [...prev, { role: 'assistant', content: 'Przepraszam, wystąpił błąd. Spróbuj ponownie za chwilę.' }]);
      }
    } catch (err) {
      setMessages((prev) => [...prev, { role: 'assistant', content: 'Nie udało się połączyć z asystentem. Sprawdź połączenie internetowe.' }]);
    } finally {
      setLoading(false);
      inputRef.current?.focus();
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const formatMessage = (text) =>
    text
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      .replace(/\[(.*?)\]\((.*?)\)/g, '<a href="$2" class="text-[var(--apisai-primary)] underline hover:opacity-80">$1</a>')
      .replace(/\n/g, '<br/>');

  return (
    <StudioShell mode="workspace" contextLabel="Pomoc">
      <div className="max-w-[1600px] mx-auto px-3 sm:px-6 py-6 lg:py-8" data-testid="help-page">
        {/* Nagłówek — lewe wyrównanie, spójny z resztą panelu */}
        <header className="space-y-1 mb-6">
          <h1 className="text-[var(--apisai-text-primary)] text-3xl sm:text-4xl font-bold tracking-tight leading-tight">Pomoc</h1>
          <p className="text-[var(--apisai-text-secondary)] text-sm sm:text-base">
            Asystent ApisAI zna całą platformę i pomoże Ci w każdej kwestii.
          </p>
        </header>

        <div className="grid grid-cols-1 xl:grid-cols-[1fr_320px] gap-6 items-start">
          {/* ─── Czat (lewa kolumna, pełna szerokość) ─── */}
          <div className="min-w-0">
            <div className="apisai-soft-card flex flex-col overflow-hidden h-[calc(100vh-220px)] min-h-[460px]">
              {/* Pasek asystenta */}
              <div className="flex items-center gap-3 px-5 py-4 border-b border-[#F0F1F3] flex-shrink-0">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'linear-gradient(135deg,#F97316,#FB923C)' }}>
                  <Bot className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-[var(--apisai-text-primary)] text-[15px] font-bold leading-tight">Asystent ApisAI</p>
                  <p className="text-[var(--apisai-text-muted)] text-[12px]">Zapytaj o cokolwiek dotyczącego systemu</p>
                </div>
              </div>

              {/* Wiadomości */}
              <div className="flex-1 overflow-y-auto space-y-4 px-5 py-5" data-testid="messages-container">
                {messages.map((msg, i) => (
                  <div
                    key={i}
                    className={`flex gap-3 ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                    data-testid={`message-${msg.role}-${i}`}
                  >
                    {msg.role === 'assistant' && (
                      <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 mt-1" style={{ background: 'linear-gradient(135deg,#F97316,#FB923C)' }}>
                        <Sparkles className="w-4 h-4 text-white" />
                      </div>
                    )}
                    <div
                      className={`max-w-[78%] rounded-2xl px-4 py-3 text-sm leading-relaxed ${
                        msg.role === 'user'
                          ? 'bg-[var(--apisai-primary)] text-white rounded-br-md'
                          : 'bg-[#F6F5F3] text-[var(--apisai-text-primary)] rounded-bl-md'
                      }`}
                      dangerouslySetInnerHTML={{ __html: formatMessage(msg.content) }}
                    />
                    {msg.role === 'user' && (
                      <div className="w-8 h-8 rounded-lg bg-zinc-200 flex items-center justify-center flex-shrink-0 mt-1">
                        <User className="w-4 h-4 text-zinc-600" />
                      </div>
                    )}
                  </div>
                ))}

                {loading && (
                  <div className="flex gap-3" data-testid="loading-indicator">
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: 'linear-gradient(135deg,#F97316,#FB923C)' }}>
                      <Sparkles className="w-4 h-4 text-white" />
                    </div>
                    <div className="bg-[#F6F5F3] rounded-2xl rounded-bl-md px-4 py-3">
                      <div className="flex items-center gap-2 text-sm text-[var(--apisai-text-muted)]">
                        <Loader2 className="w-4 h-4 animate-spin" /> Myślę...
                      </div>
                    </div>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </div>

              {/* Pole wpisywania */}
              <div className="border-t border-[#F0F1F3] px-5 py-3 flex-shrink-0">
                <div className="flex gap-2 items-end">
                  <textarea
                    ref={inputRef}
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder="Napisz pytanie..."
                    rows={1}
                    className="flex-1 resize-none rounded-xl border border-zinc-200 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--apisai-primary)]/30 focus:border-[var(--apisai-primary)]"
                    disabled={loading}
                    data-testid="assistant-input"
                  />
                  <button
                    onClick={() => sendMessage()}
                    disabled={!input.trim() || loading}
                    className="h-11 w-11 rounded-xl bg-[var(--apisai-primary)] hover:brightness-95 disabled:opacity-50 p-0 flex items-center justify-center transition-all"
                    data-testid="send-button"
                  >
                    <Send className="w-4 h-4 text-white" />
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* ─── Prawy rail ─── */}
          <aside className="space-y-4">
            <div className="apisai-soft-card p-5" data-testid="popular-questions">
              <p className="text-[var(--apisai-text-primary)] text-sm font-bold mb-3">Popularne pytania</p>
              <div className="space-y-0.5">
                {SUGGESTED_QUESTIONS.map((q, i) => (
                  <button
                    key={i}
                    onClick={() => sendMessage(q)}
                    className="w-full text-left flex items-center justify-between gap-2 py-2.5 px-2 rounded-lg text-[13px] text-[var(--apisai-text-secondary)] hover:bg-[var(--apisai-primary)]/5 hover:text-[var(--apisai-primary)] transition-colors"
                    data-testid={`suggested-q-${i}`}
                  >
                    <span>{q}</span>
                    <ChevronRight className="w-3.5 h-3.5 flex-shrink-0 opacity-50" />
                  </button>
                ))}
              </div>
            </div>

            <div className="rounded-2xl p-5" style={{ background: 'rgba(244,122,59,0.06)', border: '1px solid rgba(244,122,59,0.15)' }} data-testid="help-nudge">
              <div className="flex items-center gap-2 mb-1.5">
                <Sparkles className="w-4 h-4 text-[var(--apisai-primary)]" />
                <p className="text-[var(--apisai-text-primary)] text-sm font-bold">Asystent ApisAI 24/7</p>
              </div>
              <p className="text-[var(--apisai-text-secondary)] text-[13px] leading-relaxed">
                Zadaj dowolne pytanie o platformę — APIS odpowie od razu i podpowie, gdzie kliknąć w panelu.
              </p>
            </div>
          </aside>
        </div>
      </div>
    </StudioShell>
  );
}
